import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { LazyWrapper } from '../shared/utils/LazyLoader.js';
import { featureFlags } from '../config/featureFlags';
import PlatformAdminLogin from '../features/platform-admin/auth/PlatformAdminLogin.js';
import { SSOCallback } from '../features/auth/sso/SSOCallback.js';
import { 
  PlatformAdminDashboard,
  PlatformSettings,
  AlertThresholds
} from '../features/platform-admin/pages/index.js';
import { ThreatIntelFeeds } from '../features/admin/components/system-config/ThreatIntelFeeds.js';
import {
  HyperparameterTuning,
  LogExplorer,
  FeedbackAnalytics,
  AllFeedbackSources,
  ModelExplainability,
  ModelRegistry,
  ModelFeatureFlags,
  SnapshotManagement,
  TrainingPipeline,
  TrainingHistoryPage,
  InferenceMonitoring,
  PerformanceAnalytics,
  CalibrationMonitor,
  EnrichmentStatus,
  RetrainingJobs,
  DatasetManagement
} from '../features/platform-admin/pages/ml/index.js';
import {
  AllTenants,
  TenantHealth,
  UsageAnalytics,
  Onboarding,
  OnboardingWizard
} from '../features/platform-admin/pages/tenants/index.js';
import PlatformAdminLayout from '../features/platform-admin/layout/PlatformAdminLayout.js';
import { ProtectedRoute } from '../features/platform-admin/components/ProtectedRoute.js';
import { ErrorBoundary } from '../features/platform-admin/components/ErrorBoundary.js';
import { TenantErrorBoundaryWrapper } from '../components/TenantErrorBoundaryWrapper.js';
import {
  // Layout components (eager loading for better UX)
  MainLayout,
  TenantGuard,
  RouteGuard,
  // Authentication components (lazy loaded)
  LazyLoginPage,
  LazyLogoutPage,
  LazyUnauthorizedPage,
  // Dashboard components (lazy loaded)
  LazyAnalystDashboard,
  LazyExecutiveDashboard,
  LazyExecutiveDashboardV2,
  LazyAutonomousPerformanceDashboard,
  // Feature route components (lazy loaded)
  LazyIntegrationHubRoutes,
  LazyMachineLearningRoutes,
  LazyAPIManagementRoutes,
  LazyAdminRoutes,
  LazySupportRoutes,
  LazyAdvancedRoutes,
  LazyInvestigationRoutes,
  LazyWorkQueue,
  // Specialized feature components (lazy loaded)
  LazyThreatIntelligenceExplorer,
  LazyUserBehaviorDashboard,
  LazyComplianceIntelligenceDashboard,
  LazyCompliancePageTabs,
  LazyComplianceAssetTaggingPage,
  LazyComplianceAuditorsPage,
  LazyComplianceReportsPage,
  LazyComplianceControlsPage,
  LazyComplianceAuditsPage,
  LazyAuditManagementInterface,
  LazyPlatformAuditLog,
  LazyConnectorHealthDashboard,
  // Graph components (lazy loaded)
  LazyGraphCanvas,
  LazyEntityDetailPanel,
  LazyGraphExplorer,
  LazyGraphQueryInterface,
  // DISABLED: Rules components - Legacy SIEM paradigm (see MASTER-PRIORITY-LIST.md - OPT-06)
  // LazyVisualRuleBuilder,
  // LazyRuleManagementDashboard,
  // ML components (lazy loaded)
  LazyMLModelRegistryRedirect,
  // Threat intelligence components (lazy loaded)
  LazyThreatActorProfiles,
  LazyThreatHuntingWorkbench,
  // Playbook components (lazy loaded) - B7: Playbooks & Automation
  LazyPlaybookSuggestionQueue,
  // Decision Policy components (lazy loaded) - APB-1770: Policy Management
  LazyDecisionPolicyList,
  // Shadow AI Discovery (lazy loaded) - EPIC-007: Shadow AI Discovery
  LazyShadowAIDiscoveryRoutes,
  // Agent Registry (lazy loaded) - APB-1964: Agent Registry
  LazyAgentRegistry,
  LazyAgentDetail,
  // Regulatory Notifications (lazy loaded) - APB-1974: AI Governance
  LazyRegulatoryNotifications,
  // Threat View (Exposure) - Pentest operations platform
  LazyThreatViewRoutes
} from './LazyRoutes.js';

/**
 * Application routes component
 * Defines all routes in the application with appropriate guards
 */
export const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes - APB-100: Lazy loaded for code splitting */}
      <Route path="/login" element={
        <LazyWrapper>
          <LazyLoginPage />
        </LazyWrapper>
      } />
      <Route path="/logout" element={
        <LazyWrapper>
          <LazyLogoutPage />
        </LazyWrapper>
      } />
      <Route path="/unauthorized" element={
        <LazyWrapper>
          <LazyUnauthorizedPage />
        </LazyWrapper>
      } />
      <Route path="/" element={<Navigate to="/login" replace />} />
      
      {/* SSO Callback routes (APB-1039) - Must be public for IdP redirects */}
      <Route path="/auth/sso/callback" element={
        <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>}>
          <SSOCallback />
        </React.Suspense>
      } />
      <Route path="/auth/sso/oidc/callback" element={
        <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>}>
          <SSOCallback protocol="oidc" />
        </React.Suspense>
      } />
      <Route path="/auth/sso/saml/callback" element={
        <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>}>
          <SSOCallback protocol="saml" />
        </React.Suspense>
      } />
      
      {/* Platform Admin routes - separate from tenant routes */}
      <Route path="/platform-admin/login" element={<PlatformAdminLogin />} />
      <Route path="/platform-admin/*" element={
        <ProtectedRoute>
          <ErrorBoundary>
            <PlatformAdminLayout>
            <Routes>
              {/* Dashboard */}
              <Route path="dashboard" element={<PlatformAdminDashboard />} />
              
              {/* System Configuration */}
              <Route path="system/platform-settings" element={<PlatformSettings />} />
              <Route path="system/alert-thresholds" element={<AlertThresholds />} />
              <Route path="system/feature-flags" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Feature Flags</h1>
                  <p className="text-gray-400">Feature flag management coming soon...</p>
                </div>
              } />
              <Route path="system/threat-intel-feeds" element={<ThreatIntelFeeds />} />
              
              {/* ML Infrastructure - Migrated from tenant portal */}
              <Route path="ml/models" element={<ModelRegistry />} />
              <Route path="ml/feature-flags" element={<ModelFeatureFlags />} />
              <Route path="ml/snapshots" element={<SnapshotManagement />} />
              <Route path="ml/training" element={<TrainingPipeline />} />
              <Route path="ml/training-history" element={<TrainingHistoryPage />} />
              <Route path="ml/datasets" element={<DatasetManagement />} />
              <Route path="ml/inference" element={<InferenceMonitoring />} />
              <Route path="ml/performance" element={<PerformanceAnalytics />} />
              <Route path="ml/explainability" element={<ModelExplainability />} />
              <Route path="ml/feedback" element={<FeedbackAnalytics />} />
              <Route path="ml/tuning" element={<HyperparameterTuning />} />
              <Route path="ml/sources" element={<AllFeedbackSources />} />
              
              {/* ML Monitoring - ADMIN-001 */}
              <Route path="ml/calibration" element={<CalibrationMonitor />} />
              <Route path="ml/enrichment" element={<EnrichmentStatus />} />
              <Route path="ml/retraining" element={<RetrainingJobs />} />
              
              {/* Tenant Management */}
              <Route path="tenants/list" element={<AllTenants />} />
              <Route path="tenants/create" element={<OnboardingWizard />} />
              <Route path="tenants/health" element={<TenantHealth />} />
              <Route path="tenants/usage" element={<UsageAnalytics />} />
              <Route path="tenants/onboarding" element={<Onboarding />} />
              
              {/* Data Governance */}
              <Route path="data/retention" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Retention Policies</h1>
                  <p className="text-gray-400">Retention policies coming soon...</p>
                </div>
              } />
              <Route path="data/classification" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Data Classification</h1>
                  <p className="text-gray-400">Data classification coming soon...</p>
                </div>
              } />
              {/* Tier 1 D12: these "coming soon" placeholders were already
                  superseded by /compliance and /platform-audit-log which ship
                  real implementations. Linked from PlatformAdminSideNav.tsx
                  — keep the routes so the nav doesn't 404, but redirect to
                  the real pages instead of advertising a "coming soon" stub. */}
              <Route path="data/audit-logs" element={<Navigate to="/platform-audit-log" replace />} />
              <Route path="data/compliance" element={<Navigate to="/compliance" replace />} />
              
              {/* Platform Monitoring */}
              <Route path="monitoring/health" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">System Health</h1>
                  <p className="text-gray-400">System health monitoring coming soon...</p>
                </div>
              } />
              <Route path="monitoring/infrastructure" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Infrastructure Metrics</h1>
                  <p className="text-gray-400">Infrastructure metrics coming soon...</p>
                </div>
              } />
              <Route path="monitoring/api" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">API Performance</h1>
                  <p className="text-gray-400">API performance monitoring coming soon...</p>
                </div>
              } />
              <Route path="monitoring/database" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Database Performance</h1>
                  <p className="text-gray-400">Database performance monitoring coming soon...</p>
                </div>
              } />
              <Route path="monitoring/logs" element={<LogExplorer />} />
              
              {/* Profile */}
              <Route path="profile" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Profile Settings</h1>
                  <p className="text-gray-400">This feature is coming soon...</p>
                </div>
              } />
              
              {/* Platform Admins */}
              <Route path="admins/users" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Admin Users</h1>
                  <p className="text-gray-400">Admin user management coming soon...</p>
                </div>
              } />
              <Route path="admins/permissions" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Permissions</h1>
                  <p className="text-gray-400">Permission management coming soon...</p>
                </div>
              } />
              <Route path="admins/activity" element={
                <div className="p-8">
                  <h1 className="text-2xl font-bold text-white mb-4">Activity Log</h1>
                  <p className="text-gray-400">Activity log coming soon...</p>
                </div>
              } />
              
              {/* Default redirect */}
              <Route path="*" element={<Navigate to="/platform-admin/dashboard" replace />} />
            </Routes>
            </PlatformAdminLayout>
          </ErrorBoundary>
        </ProtectedRoute>
      } />
      
      {/* Protected routes - require tenant selection */}
      <Route element={<TenantGuard />}>
        <Route element={<TenantErrorBoundaryWrapper />}>
          {/* Analyst routes - APB-100: Lazy loaded */}
          <Route path="/analyst" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAnalystDashboard />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Executive routes - APB-100: Lazy loaded */}
        {/* V2 is now the default executive dashboard */}
        <Route path="/executive" element={
          <RouteGuard allowedRoles={['admin', 'executive']}>
            <MainLayout>
              <LazyWrapper>
                <LazyExecutiveDashboardV2 />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Legacy Executive Dashboard - kept for backward compatibility */}
        <Route path="/executive-legacy" element={
          <RouteGuard allowedRoles={['admin', 'executive']}>
            <MainLayout>
              <LazyWrapper>
                <LazyExecutiveDashboard />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Autonomous Performance Dashboard - Executive monitoring */}
        <Route path="/executive/autonomous-performance" element={
          <RouteGuard allowedRoles={['admin', 'executive']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAutonomousPerformanceDashboard />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Integration hub routes - APB-100: Lazy loaded */}
        <Route path="/integration-hub/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyIntegrationHubRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Machine Learning routes - APB-918: Lazy loaded */}
        <Route path="/integration-hub/machine-learning/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyMachineLearningRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* API management routes - APB-100: Lazy loaded */}
        <Route path="/api/*" element={
          <RouteGuard allowedRoles={['admin']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAPIManagementRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Support routes - APB-100: Lazy loaded */}
        <Route path="/support/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst', 'executive']}>
            <MainLayout>
              <LazyWrapper>
                <LazySupportRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Admin routes - APB-100: Lazy loaded */}
        <Route path="/admin/*" element={
          <RouteGuard allowedRoles={['admin']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAdminRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Advanced routes - APB-100: Lazy loaded */}
        <Route path="/advanced/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAdvancedRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Investigation routes - APB-100: Lazy loaded */}
        <Route path="/investigation/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyInvestigationRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Work Queue - Analyst claimed alerts and investigation workflow */}
        <Route path="/tickets" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <React.Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div></div>}>
                <LazyWorkQueue />
              </React.Suspense>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Threat Intelligence Explorer - APB-100: Lazy loaded */}
        <Route path="/threat-intelligence" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyThreatIntelligenceExplorer />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Threat Actor Profiles - APB-100: Lazy loaded */}
        <Route path="/threat-intelligence/actors" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyThreatActorProfiles />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Threat Hunting Workbench - APB-100: Lazy loaded */}
        <Route path="/threat-intelligence/hunting" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyThreatHuntingWorkbench />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Playbooks & Automation - B7: Lazy loaded */}
        <Route path="/playbooks" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyPlaybookSuggestionQueue />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Decision Policies - APB-1770: Policy list + CRUD */}
        <Route path="/decision-policies" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyDecisionPolicyList />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Shadow AI Discovery - EPIC-007: Shadow agent management */}
        <Route path="/shadow-ai/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyShadowAIDiscoveryRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Agent Registry - APB-1964: Autonomous agent governance */}
        <Route path="/agent-registry" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAgentRegistry />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        <Route path="/agent-registry/:agentId" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAgentDetail />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Regulatory Notifications - APB-1974: AI Governance Notifications */}
        <Route path="/regulatory-notifications" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyRegulatoryNotifications />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* User Behavior Dashboard - APB-100: Lazy loaded */}
        <Route path="/user-behavior" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyUserBehaviorDashboard />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Compliance — tabbed: Alert Intelligence (real-time) + Framework Status.
            Wraps the previously-orphaned ComplianceStatusDashboard as a tab so
            the file is no longer dead code. The intelligence dashboard remains
            the default. */}
        <Route path="/compliance" element={
          <RouteGuard allowedRoles={['admin', 'analyst', 'executive']}>
            <MainLayout>
              <LazyWrapper>
                <LazyCompliancePageTabs />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Sprint 3 D5-rev: Compliance Asset Tagging (CMDB overlay, NOT manual CRUD).
            Read-only view of CMDB-imported assets with per-asset + bulk framework
            tagging. The killed D5 was wrong architecture (duplicated CMDB); this
            is Pattern B from the master roadmap. */}
        <Route path="/compliance/asset-tagging" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyComplianceAssetTaggingPage />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Sprint 3 D9: External Auditor Management (admin-only).
            Invite Deloitte/EY/KPMG etc. for scoped time-bounded SOC 2/PCI/HIPAA
            attestation access. Pairs with F1 backend (auditor_invitations +
            auditor_grants tables already exist via migration 130). */}
        <Route path="/compliance/auditors" element={
          <RouteGuard allowedRoles={['admin']}>
            <MainLayout>
              <LazyWrapper>
                <LazyComplianceAuditorsPage />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Sprint 3 D7: Compliance Reports — scheduling + history.
            Backend (compliance_report_schedules + history tables + scheduler)
            ships in Sprint 4. UI displays "backend pending" callout if 404. */}
        <Route path="/compliance/reports" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyComplianceReportsPage />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Sprint 3 D8: Control Mapping — BRAIN actions ↔ framework control numbers.
            Backend (compliance_control_mappings table + framework seed data) ships
            in Sprint 4 with default mappings auto-seeded per framework. */}
        <Route path="/compliance/controls" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyComplianceControlsPage />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Sprint 3 D6: Audit Programs (replaces old /audit-management placeholder).
            Schedule audit windows, collect evidence, track findings + remediation.
            Backend (audit_programs + audit_evidence + audit_findings tables) ships
            in Sprint 4. UI shows "backend pending" callout if 404. */}
        <Route path="/compliance/audits" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyComplianceAuditsPage />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />

        {/* Audit Management Interface - APB-100: Lazy loaded */}
        <Route path="/audit-management" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyAuditManagementInterface />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Platform Audit Log - SOAR Action Audit Trail */}
        <Route path="/platform-audit-log" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyPlatformAuditLog />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Connector Health Dashboard - Diagnostic UI for connector troubleshooting */}
        <Route path="/connectors/health" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyConnectorHealthDashboard />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* Graph Canvas - SOFT DEPRECATED 2026-01-02 - Hidden for 180 days */}
        {featureFlags.enableGraphCanvas && (
          <Route path="/graph" element={
            <RouteGuard allowedRoles={['admin', 'analyst']}>
              <MainLayout>
                <LazyWrapper>
                  <LazyGraphCanvas />
                </LazyWrapper>
              </MainLayout>
            </RouteGuard>
          } />
        )}
        
        {/* Entity Detail Panel - SOFT DEPRECATED 2026-01-02 - Hidden for 180 days */}
        {featureFlags.enableEntityDetailRoute && (
          <Route path="/entity/:entityId" element={
            <RouteGuard allowedRoles={['admin', 'analyst']}>
              <MainLayout>
                <LazyWrapper>
                  <LazyEntityDetailPanel />
                </LazyWrapper>
              </MainLayout>
            </RouteGuard>
          } />
        )}
        
        {/* Graph Explorer - SOFT DEPRECATED 2026-01-02 - Hidden for 180 days */}
        {featureFlags.enableGraphExplorerRoute && (
          <Route path="/graph-explorer" element={
            <RouteGuard allowedRoles={['admin', 'analyst']}>
              <MainLayout>
                <LazyWrapper>
                  <LazyGraphExplorer />
                </LazyWrapper>
              </MainLayout>
            </RouteGuard>
          } />
        )}
        
        {/* Graph Query Interface - SOFT DEPRECATED 2026-01-02 - Hidden for 180 days */}
        {featureFlags.enableQueryBuilder && (
          <Route path="/graph-query" element={
            <RouteGuard allowedRoles={['admin', 'analyst']}>
              <MainLayout>
                <LazyWrapper>
                  <LazyGraphQueryInterface />
                </LazyWrapper>
              </MainLayout>
            </RouteGuard>
          } />
        )}
        
        {/* DISABLED: Rules Management - Legacy SIEM paradigm (see MASTER-PRIORITY-LIST.md - OPT-06) */}
        {/* Future: Transform to Policy-as-Code for autonomous guardrails */}
        {/* <Route path="/rules/builder" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyVisualRuleBuilder />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } /> */}
        
        {/* <Route path="/rules/management" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyRuleManagementDashboard />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } /> */}
        
        {/* ML Model Registry - APB-918: Lazy loaded */}
        <Route path="/ml/model-registry" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyMLModelRegistryRedirect />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        
        {/* API ML Model Registry - Backward compatibility */}
        <Route path="/api/ml-models" element={
          <RouteGuard allowedRoles={['admin']}>
            <MainLayout>
              <LazyWrapper>
                <LazyMLModelRegistryRedirect />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        </Route>
      </Route>

      {/* Threat View (Exposure) — Pentest operations platform */}
      <Route element={<TenantGuard />}>
        <Route element={<TenantErrorBoundaryWrapper />}>
          <Route path="/threat-view/*" element={
          <RouteGuard allowedRoles={['admin', 'analyst']}>
            <MainLayout>
              <LazyWrapper>
                <LazyThreatViewRoutes />
              </LazyWrapper>
            </MainLayout>
          </RouteGuard>
        } />
        </Route>
      </Route>

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;
